import type { GiftListStatus } from "@/lib/gift-lists/gift-list-schemas";
import type { ListLifecycle } from "@/lib/host-panel/host-panel-types";

// ─── Mappers ─────────────────────────────────────────────────────────────────

/**
 * Normalizes any date input to DD/MM/YYYY (backend contract).
 *
 * Accepts: yyyy-MM-dd (standard), yyyy-MM-ddTHH:mm:ssZ (datetime), DD/MM/YYYY (passthrough).
 * Throws on unparseable input — fail loud instead of corrupting silently.
 */
export function toBackendEventDate(input: string): string {
  // Already in backend format — passthrough.
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(input)) return input;

  // ISO date or datetime — extract date part.
  const isoMatch = input.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!isoMatch) {
    throw new Error(`toBackendEventDate: unexpected format "${input}"`);
  }

  const [, year, month, day] = isoMatch;
  return `${day}/${month}/${year}`;
}

/**
 * Maps the backend's `GiftListStatus` to the panel's `ListLifecycle`.
 *
 * Decisions 1 and 1b of the integration spec (2026-09-16):
 *   DRAFT               → rascunho  (not published; a panel state the prototype never drew)
 *   PUBLISHED / ACTIVE  → nova      while no gift has been paid for, `ativa` once one has
 *   INACTIVE            → pausada   (reachable by the owner through `pause`, so it needs a name)
 *   CLOSED              → encerrada
 *
 * "Paid for" rather than "any order": an abandoned or cancelled order is not a gift received,
 * and `nova` is the state whose copy still invites the host to share the link.
 */
export function fromBackendGiftListStatus(
  status: GiftListStatus,
  activity: { hasPaidOrders: boolean },
): ListLifecycle {
  switch (status) {
    case "DRAFT":
      return "rascunho";
    case "PUBLISHED":
    case "ACTIVE":
      return activity.hasPaidOrders ? "ativa" : "nova";
    case "INACTIVE":
      return "pausada";
    case "CLOSED":
      return "encerrada";
  }
}
